import { Link } from "react-router-dom";
import { 
  Zap, Wifi, Smartphone, Shield, CreditCard, 
  Menu, X, ChevronDown
} from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { name: "Stromtarife", slug: "strom", icon: Zap },
  { name: "DSL & Internet", slug: "dsl-internet", icon: Wifi },
  { name: "Handyverträge", slug: "handyvertrag", icon: Smartphone },
  { name: "Versicherungen", slug: "versicherungen", icon: Shield },
  { name: "Kredit & Kreditkarten", slug: "kredit-kreditkarte", icon: CreditCard },
];

// AdSense Component
const AdSlot = ({ slotId, format = "auto", className = "" }) => {
  useEffect(() => {
    try {
      if (window.adsbygoogle && slotId) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      console.error("AdSense error:", e);
    }
  }, [slotId]);

  if (!slotId) return null;

  return (
    <div className={`ad-container ${className}`}>
      <ins
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-format={format}
        data-ad-slot={slotId}
        data-full-width-responsive="true"
      />
    </div>
  );
};

export default function PublicLayout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [googleSettings, setGoogleSettings] = useState(null);
  const [adSlots, setAdSlots] = useState([]);

  useEffect(() => {
    // Fetch Google settings
    const fetchGoogleSettings = async () => {
      try {
        const [settingsRes, slotsRes] = await Promise.all([
          axios.get(`${API}/settings/public/google`),
          axios.get(`${API}/settings/public/ad-slots`)
        ]);
        setGoogleSettings(settingsRes.data);
        setAdSlots(slotsRes.data || []);

        // Inject Google Analytics
        if (settingsRes.data?.analytics_id) {
          const script = document.createElement('script');
          script.src = `https://www.googletagmanager.com/gtag/js?id=${settingsRes.data.analytics_id}`;
          script.async = true;
          document.head.appendChild(script);

          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', settingsRes.data.analytics_id);
        }

        // Inject Tag Manager
        if (settingsRes.data?.tag_manager_id) {
          const script = document.createElement('script');
          script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${settingsRes.data.tag_manager_id}');`;
          document.head.appendChild(script);
        }

        // Inject AdSense
        if (settingsRes.data?.adsense_client_id) {
          const script = document.createElement('script');
          script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${settingsRes.data.adsense_client_id}`;
          script.async = true;
          script.crossOrigin = "anonymous";
          document.head.appendChild(script);
        }

        // Inject Search Console verification
        if (settingsRes.data?.search_console_verification) {
          const meta = document.createElement('meta');
          meta.name = "google-site-verification";
          meta.content = settingsRes.data.search_console_verification;
          document.head.appendChild(meta);
        }
      } catch (error) {
        console.error("Error fetching Google settings:", error);
      }
    };
    fetchGoogleSettings();
  }, []);

  const getAdSlot = (name) => adSlots.find(s => s.name === name);
  const headerAd = getAdSlot("header");
  const footerAd = getAdSlot("footer");

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-10 h-10 bg-electric-500 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">B</span>
              </div>
              <span className="font-heading font-bold text-xl text-navy-900 hidden sm:block">
                BestTariffeSed
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="font-medium" data-testid="nav-categories-trigger">
                    Vergleichen
                    <ChevronDown className="ml-1 w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-56">
                  {categories.map((cat) => (
                    <DropdownMenuItem key={cat.slug} asChild>
                      <Link 
                        to={`/${cat.slug}`} 
                        className="flex items-center gap-3 cursor-pointer"
                        data-testid={`nav-link-${cat.slug}`}
                      >
                        <cat.icon className="w-4 h-4 text-electric-500" />
                        {cat.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {categories.slice(0, 3).map((cat) => (
                <Button key={cat.slug} variant="ghost" className="font-medium" asChild>
                  <Link to={`/${cat.slug}`}>
                    {cat.name}
                  </Link>
                </Button>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white">
            <nav className="container mx-auto px-4 py-4 space-y-1">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/${cat.slug}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-50 text-navy-900 font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${cat.slug}`}
                >
                  <cat.icon className="w-5 h-5 text-electric-500" />
                  {cat.name}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Header Ad Slot */}
      {headerAd && (
        <div className="bg-slate-100 py-2">
          <div className="container mx-auto px-4">
            <AdSlot slotId={headerAd.slot_id} format={headerAd.format} className="text-center" />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer Ad Slot */}
      {footerAd && (
        <div className="bg-slate-100 py-4 border-t border-slate-200">
          <div className="container mx-auto px-4">
            <AdSlot slotId={footerAd.slot_id} format={footerAd.format} className="text-center" />
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-navy-900 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Brand */}
            <div>
              <Link to="/" className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-electric-500 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-lg">B</span>
                </div>
                <span className="font-heading font-bold text-xl">
                  BestTariffeSed
                </span>
              </Link>
              <p className="text-slate-400 text-sm">
                Ihr unabhängiger Vergleichspartner für die besten Tarife in Deutschland.
              </p>
            </div>

            {/* Categories */}
            <div>
              <h3 className="font-heading font-bold text-lg mb-4">Kategorien</h3>
              <ul className="space-y-2">
                {categories.map((cat) => (
                  <li key={cat.slug}>
                    <Link 
                      to={`/${cat.slug}`} 
                      className="text-slate-400 hover:text-white transition-colors text-sm"
                    >
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="font-heading font-bold text-lg mb-4">Rechtliches</h3>
              <ul className="space-y-2">
                <li>
                  <Link 
                    to="/impressum" 
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                    data-testid="footer-impressum"
                  >
                    Impressum
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/datenschutz" 
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                    data-testid="footer-datenschutz"
                  >
                    Datenschutz
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-heading font-bold text-lg mb-4">Kontakt</h3>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>info@besttariffesed.de</li>
                <li>+49 (0) 30 12345678</li>
                <li>Berlin, Deutschland</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-8 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} BestTariffeSed.de. Alle Rechte vorbehalten.
            </p>
            <p className="text-slate-500 text-xs">
              * Affiliate-Links: Bei Vertragsabschluss erhalten wir eine Provision.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
